import {
    applyFurnizorPricingToRoot,
    builderFormToCalculatorInput,
    calculateProductPricing,
    calculateTransportCostRate,
    DEFAULT_PRICING_SETTINGS,
    entryRonExVatBeforeCostUplift,
    formatNumericInputValue,
    getDefaultFurnizorPricing,
    LIST_PRICE_EURO_DIVISOR,
    mergeFormWithFurnizorPricing,
    mergeListPricingSettings,
    parseNumericInput,
    persistActiveFurnizorPricingFromRoot,
    ratioToPercentageInput,
    roundMoney,
} from '@/utils/priceListProductCalculator';
import {
    normalizeCurrencyCode,
    normalizeTransportCostBreakdown,
    resolveTransportConverterRate,
    syncTransportCostFromBreakdown,
    TRANSPORT_COST_MODE_FORMULA,
    TRANSPORT_COST_MODE_PERCENT,
} from '@/utils/transportCostBreakdown';
import {
    getStampDutyAmountRon,
    normalizeStampDutyBreakdown,
    normalizeStampDutyCurrency,
    STAMP_DUTY_MODE_FORMULA,
    STAMP_DUTY_MODE_MANUAL,
    syncStampDutyFromBreakdown,
} from '@/utils/stampDutyBreakdown';

/**
 * Convert supplier list prices when the working currency changes, using the
 * exchange rates configured on the price list (not daily BNR/ECB rates).
 *
 * @param {Record<string, unknown>} form
 * @param {string} fromCurrency
 * @param {string} toCurrency
 * @param {Record<string, unknown>} [pricingSettings]
 * @param {{ convertNew?: boolean, convertOld?: boolean }} [options]
 * @returns {boolean} true when at least one price field was rewritten
 */
export function convertSupplierListPricesForCurrencyChange(
    form,
    fromCurrency,
    toCurrency,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
    options = {},
) {
    const from = normalizeCurrencyCode(fromCurrency || 'ron');
    const to = normalizeCurrencyCode(toCurrency || 'ron');
    if (from === to) {
        return false;
    }

    const convertNew = options.convertNew !== false;
    const convertOld = options.convertOld !== false;
    if (!convertNew && !convertOld) {
        return false;
    }

    const settings = mergeListPricingSettings(pricingSettings);
    const rate = resolveTransportConverterRate(from, to, settings);
    if (!(rate > 0)) {
        return false;
    }

    let changed = false;
    const convertField = (fieldKey) => {
        const raw = form[fieldKey];
        if (raw === null || raw === undefined || raw === '') {
            return;
        }
        const value = parseNumericInput(raw);
        if (!(value > 0)) {
            return;
        }
        const next = formatNumericInputValue(roundMoney(value * rate, 4), 4);
        if (String(raw) !== String(next)) {
            form[fieldKey] = next;
            changed = true;
        }
    };

    if (convertNew) {
        convertField('supplierListPriceNew');
    }
    if (convertOld) {
        convertField('supplierListPriceOld');
    }
    return changed;
}

/**
 * Convert currency-denominated fields on a pricing baseline snapshot so
 * %-change badges do not treat a unit conversion as a price jump
 * (e.g. 100 EUR → 535 RON must not show +435%).
 *
 * Only amounts expressed in the supplier working currency are converted.
 * RON result fields (entry/list) stay as-is.
 *
 * @param {Record<string, unknown>|null|undefined} baseline
 * @param {string} fromCurrency
 * @param {string} toCurrency
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {Record<string, unknown>|null|undefined}
 */
export function convertPricingBaselineForCurrencyChange(
    baseline,
    fromCurrency,
    toCurrency,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
) {
    if (!baseline || typeof baseline !== 'object') {
        return baseline;
    }
    const from = normalizeCurrencyCode(fromCurrency || 'ron');
    const to = normalizeCurrencyCode(toCurrency || 'ron');
    if (from === to) {
        return baseline;
    }

    const settings = mergeListPricingSettings(pricingSettings);
    const rate = resolveTransportConverterRate(from, to, settings);
    if (!(rate > 0)) {
        return baseline;
    }

    const convertAmount = (raw, decimals = 4) => {
        if (raw === null || raw === undefined || raw === '') {
            return raw;
        }
        const value = parseNumericInput(raw);
        if (!(value > 0)) {
            return raw;
        }
        return formatNumericInputValue(roundMoney(value * rate, decimals), decimals);
    };

    return {
        ...baseline,
        currency: to,
        // Supplier working-currency amounts (not RON results).
        supplierListPriceNew: convertAmount(baseline.supplierListPriceNew, 4),
        supplierListPriceOld: Object.prototype.hasOwnProperty.call(
            baseline,
            'supplierListPriceOld',
        )
            ? convertAmount(baseline.supplierListPriceOld, 4)
            : baseline.supplierListPriceOld,
        finalEntryPriceExVat: convertAmount(baseline.finalEntryPriceExVat, 4),
        netEntryPriceExVat: Object.prototype.hasOwnProperty.call(
            baseline,
            'netEntryPriceExVat',
        )
            ? convertAmount(baseline.netEntryPriceExVat, 4)
            : baseline.netEntryPriceExVat,
    };
}

/**
 * Convert a single supplier-list amount with list exchange rates.
 *
 * @param {unknown} raw
 * @param {string} fromCurrency
 * @param {string} toCurrency
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {unknown}
 */
export function convertSupplierListPriceAmount(
    raw,
    fromCurrency,
    toCurrency,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
) {
    if (raw === null || raw === undefined || raw === '') {
        return raw;
    }
    const from = normalizeCurrencyCode(fromCurrency || 'ron');
    const to = normalizeCurrencyCode(toCurrency || 'ron');
    if (from === to) {
        return raw;
    }
    const value = parseNumericInput(raw);
    if (!(value > 0)) {
        return raw;
    }
    const rate = resolveTransportConverterRate(
        from,
        to,
        mergeListPricingSettings(pricingSettings),
    );
    if (!(rate > 0)) {
        return raw;
    }
    return formatNumericInputValue(roundMoney(value * rate, 4), 4);
}

/** Computed result keys shown in the builder "Rezultate calculate" section. */
export const PRICING_RESULT_FIELD_KEYS = [
    'finalEntryPriceExVat',
    'entryPriceRonExVat',
    'entryPriceRonIncVat',
    'listPriceRonExVat',
    'listPriceRonIncVatFinal',
    'listPriceEuroExVat',
];

/** Result keys derived only from forward calculation — not user-editable. */
export const PRICING_READONLY_RESULT_FIELD_KEYS = [
    'finalEntryPriceExVat',
    'entryPriceRonExVat',
    'entryPriceRonIncVat',
    'listPriceEuroExVat',
];

/** Input keys whose edits drive forward pricing calculations. */
export const PRICING_INPUT_FIELD_KEYS = [
    'supplierListPriceNew',
    'currency',
    'discounts',
    'discount1',
    'discount2',
    'cashDiscount',
    'marketingDiscount',
    'transportCostRate',
    'transportCostBreakdown',
    'stampDutyAmount',
    'stampDutyCurrency',
    'stampDutyBreakdown',
    'squareMetersPerTruck',
    'markupReference',
    'markupActual',
];

/**
 * @param {Record<string, unknown>} form
 * @returns {number}
 */
export function netEntryMultiplierFromForm(form) {
    const input = builderFormToCalculatorInput(form);
    let multiplier = 1;

    const discountRatios = Array.isArray(input.discounts) && input.discounts.length
        ? input.discounts.map((value) => parseNumericInput(value)).filter((ratio) => ratio > 0)
        : [
            parseNumericInput(input.discount1),
            parseNumericInput(input.discount2),
            parseNumericInput(input.cashDiscount),
        ].filter((ratio) => ratio > 0);

    discountRatios.forEach((ratio) => {
        multiplier *= (1 - ratio);
    });

    const marketingRatio = parseNumericInput(input.marketingDiscount);
    if (marketingRatio > 0) {
        multiplier *= (1 + marketingRatio);
    }

    return multiplier > 0 ? multiplier : 1;
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} netEntryExVat
 * @returns {number}
 */
export function supplierListPriceFromNetEntry(form, netEntryExVat) {
    const netEntry = parseNumericInput(netEntryExVat);
    if (!(netEntry > 0)) {
        return 0;
    }
    return roundMoney(netEntry / netEntryMultiplierFromForm(form), 4);
}

/**
 * @param {unknown} netEntryExVat
 * @param {unknown} transportCostRate
 * @returns {number}
 */
export function finalEntryFromNetEntry(netEntryExVat, transportCostRate) {
    const netEntry = parseNumericInput(netEntryExVat);
    const transportRate = parseNumericInput(transportCostRate);
    if (!(netEntry > 0)) {
        return 0;
    }
    return roundMoney(netEntry * (1 + transportRate), 4);
}

/**
 * @param {unknown} finalEntryExVat
 * @param {unknown} transportCostRate
 * @returns {number}
 */
export function netEntryFromFinalEntry(finalEntryExVat, transportCostRate) {
    const finalEntry = parseNumericInput(finalEntryExVat);
    const transportRate = parseNumericInput(transportCostRate);
    const divisor = 1 + transportRate;
    if (!(finalEntry > 0) || divisor <= 0) {
        return 0;
    }
    return roundMoney(finalEntry / divisor, 4);
}

/**
 * @param {unknown} finalEntryExVat
 * @param {string} currency
 * @param {Record<string, unknown>} pricingSettings
 * @returns {number}
 */
export function entryPriceRonExVatFromFinalEntry(finalEntryExVat, currency, pricingSettings) {
    const computed = calculateProductPricing(
        {
            supplierListPriceNew: parseNumericInput(finalEntryExVat),
            currency,
            transportCostRate: 0,
            markupReference: 0,
        },
        pricingSettings,
    );
    const normalizedCurrency = String(currency || 'ron').trim().toLowerCase();
    if (normalizedCurrency === 'ron') {
        return roundMoney(parseNumericInput(finalEntryExVat), 2);
    }
    const appliedRate = parseNumericInput(computed.appliedExchangeRate);
    if (appliedRate <= 0) {
        return 0;
    }
    return roundMoney(parseNumericInput(finalEntryExVat) * appliedRate, 2);
}

/**
 * @param {unknown} entryPriceRonExVat
 * @param {string} currency
 * @param {Record<string, unknown>} pricingSettings
 * @returns {number}
 */
export function finalEntryFromEntryPriceRonExVat(entryPriceRonExVat, currency, pricingSettings) {
    const entryRon = parseNumericInput(entryPriceRonExVat);
    if (!(entryRon > 0)) {
        return 0;
    }
    const normalizedCurrency = String(currency || 'ron').trim().toLowerCase();
    if (normalizedCurrency === 'ron') {
        return roundMoney(entryRon, 4);
    }
    const settings = mergeListPricingSettings(pricingSettings);
    const probe = calculateProductPricing(
        {
            supplierListPriceNew: 1,
            currency,
            transportCostRate: 0,
            markupReference: 0,
        },
        settings,
    );
    const appliedRate = parseNumericInput(probe.appliedExchangeRate);
    if (appliedRate <= 0) {
        return 0;
    }
    return roundMoney(entryRon / appliedRate, 4);
}

/**
 * @param {{
 *   transportCostRate?: unknown,
 *   supplierListPriceNew?: unknown,
 *   squareMetersPerTruck?: unknown,
 * }} params
 * @returns {number}
 */
export function calculateTransportCostEstimated({
    transportCostRate,
    supplierListPriceNew,
    squareMetersPerTruck,
}) {
    const rate = parseNumericInput(transportCostRate);
    const supplierPrice = parseNumericInput(supplierListPriceNew);
    const squareMeters = parseNumericInput(squareMetersPerTruck);
    if (rate <= 0 || supplierPrice <= 0) {
        return 0;
    }
    if (squareMeters > 0) {
        return roundMoney(rate * supplierPrice * squareMeters, 2);
    }
    return roundMoney(rate * supplierPrice, 2);
}

/**
 * @param {Record<string, unknown>} form
 */
export function clearListPriceOverride(form) {
    form.overrideListPriceRonIncVatFinal = false;
    form.overrideListPriceRonIncVatFinalValue = '';
    form.useManualFinalPrice = false;
    form.manualListPriceRonIncVatFinal = '';
    form.priceAdjustmentRatio = '';
}

/** Session-only snapshot so lock check→uncheck restores prior adaos/sticky state. */
export const LOCK_MARKUP_RESTORE_SNAPSHOT_KEY = '_lockMarkupToReferenceRestore';

/**
 * @param {Record<string, unknown>} form
 */
function captureLockMarkupRestoreSnapshot(form) {
    form[LOCK_MARKUP_RESTORE_SNAPSHOT_KEY] = {
        markupActual: form.markupActual ?? '',
        overrideListPriceRonIncVatFinal: Boolean(
            form.overrideListPriceRonIncVatFinal,
        ),
        overrideListPriceRonIncVatFinalValue:
            form.overrideListPriceRonIncVatFinalValue ?? '',
        useManualFinalPrice: Boolean(form.useManualFinalPrice),
        manualListPriceRonIncVatFinal: form.manualListPriceRonIncVatFinal ?? '',
        priceAdjustmentRatio: form.priceAdjustmentRatio ?? '',
    };
}

/**
 * @param {Record<string, unknown>} form
 */
export function clearLockMarkupRestoreSnapshot(form) {
    if (form && Object.prototype.hasOwnProperty.call(form, LOCK_MARKUP_RESTORE_SNAPSHOT_KEY)) {
        delete form[LOCK_MARKUP_RESTORE_SNAPSHOT_KEY];
    }
}

/**
 * @param {Record<string, unknown>} form
 * @returns {boolean}
 */
function restoreLockMarkupSnapshot(form) {
    const snap = form?.[LOCK_MARKUP_RESTORE_SNAPSHOT_KEY];
    if (!snap || typeof snap !== 'object') {
        return false;
    }
    form.markupActual = snap.markupActual ?? '';
    form.overrideListPriceRonIncVatFinal = Boolean(
        snap.overrideListPriceRonIncVatFinal,
    );
    form.overrideListPriceRonIncVatFinalValue =
        snap.overrideListPriceRonIncVatFinalValue ?? '';
    form.useManualFinalPrice = Boolean(snap.useManualFinalPrice);
    form.manualListPriceRonIncVatFinal = snap.manualListPriceRonIncVatFinal ?? '';
    form.priceAdjustmentRatio = snap.priceAdjustmentRatio ?? '';
    clearLockMarkupRestoreSnapshot(form);
    return true;
}

/**
 * Copy adaos referință → adaos actual and drop sticky list override so selling
 * price follows entry cost at the reference margin.
 *
 * @param {Record<string, unknown>} form
 */
export function syncMarkupActualToReference(form) {
    clearListPriceOverride(form);
    const reference = String(form.markupReference ?? '').trim();
    if (reference === '') {
        form.markupActual = '';
        return;
    }
    form.markupActual = formatNumericInputValue(parseNumericInput(reference), 2);
}

/**
 * @param {Record<string, unknown>} form
 * @returns {boolean}
 */
export function isMarkupLockedToReference(form) {
    return Boolean(form?.lockMarkupToReference);
}

/**
 * Locks the current list price so upstream edits cannot recalculate it.
 * Prefers an existing override; otherwise uses a pre-edit snapshot.
 *
 * @param {Record<string, unknown>} form
 * @param {unknown} [preservedListPriceRonIncVatFinal]
 * @returns {boolean}
 */
export function preserveListPriceOverride(form, preservedListPriceRonIncVatFinal) {
    const existingOverride = parseNumericInput(form.overrideListPriceRonIncVatFinalValue);
    if (form.overrideListPriceRonIncVatFinal && existingOverride > 0) {
        return true;
    }
    const listPrice = parseNumericInput(preservedListPriceRonIncVatFinal);
    if (!(listPrice > 0)) {
        return false;
    }
    form.overrideListPriceRonIncVatFinal = true;
    form.overrideListPriceRonIncVatFinalValue = formatNumericInputValue(listPrice, 2);
    return true;
}

/**
 * Derive adaos ratio from sticky/final listă vs current entry (money), ignoring
 * any stored markupActual. Calculator prefers stored % when both sticky+markup
 * coexist (bulk exact %), so sync must not use computedMarkupActual here.
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} pricingSettings
 * @returns {number|null} ratio, or null when list/entry cannot derive
 */
function deriveMarkupActualRatioFromListVsEntry(form, pricingSettings) {
    const computed = calculateFromForm(form, pricingSettings);
    const entry = parseNumericInput(computed.entryPriceRonIncVat);
    const list = parseNumericInput(computed.listPriceRonIncVatFinal);
    if (!(entry > 0) || !(list > 0)) {
        return null;
    }
    return (list - entry) / entry;
}

/**
 * Derives adaos actual from sticky list price vs current entry price.
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} [pricingSettings]
 */
export function syncMarkupActualFromListOverride(form, pricingSettings = DEFAULT_PRICING_SETTINGS) {
    if (!form.overrideListPriceRonIncVatFinal) {
        return;
    }
    if (!(parseNumericInput(form.overrideListPriceRonIncVatFinalValue) > 0)) {
        return;
    }
    const derived = deriveMarkupActualRatioFromListVsEntry(form, pricingSettings);
    if (derived == null) {
        return;
    }
    form.markupActual = ratioToPercentageInput(derived);
}

/**
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} [pricingSettings]
 */
export function syncTransportCostRateFromEstimated(form, pricingSettings = DEFAULT_PRICING_SETTINGS) {
    void pricingSettings;
    const estimated = String(form.transportCostEstimated ?? '').trim();
    if (!estimated) {
        return;
    }
    const rate = calculateTransportCostRate({
        transportCostEstimated: estimated,
        supplierListPriceNew: form.supplierListPriceNew,
        squareMetersPerTruck: form.squareMetersPerTruck,
    });
    if (rate <= 0) {
        form.transportCostRate = '';
        return;
    }
    const currentRatePercent = parseNumericInput(form.transportCostRate);
    const nextRatePercent = roundMoney(rate * 100, 2);
    if (
        currentRatePercent > 0 &&
        Math.abs(nextRatePercent - currentRatePercent) < 0.005
    ) {
        return;
    }
    form.transportCostRate = ratioToPercentageInput(rate, 2);
}

/**
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} pricingSettings
 * @returns {Record<string, unknown>}
 */
function calculateFromForm(form, pricingSettings) {
    return calculateProductPricing(
        builderFormToCalculatorInput(form),
        mergeListPricingSettings(pricingSettings),
    );
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyNetEntryPriceEdit(form, value, pricingSettings) {
    void pricingSettings;
    const supplierNew = supplierListPriceFromNetEntry(form, value);
    form.supplierListPriceNew = supplierNew > 0
        ? formatNumericInputValue(supplierNew, 4)
        : '';
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyFinalEntryPriceEdit(form, value, pricingSettings) {
    void pricingSettings;
    const input = builderFormToCalculatorInput(form);
    const netEntry = netEntryFromFinalEntry(value, input.transportCostRate);
    applyNetEntryPriceEdit(form, netEntry, pricingSettings);
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyEntryPriceRonExVatEdit(form, value, pricingSettings) {
    const entryRonWithUplifts = parseNumericInput(value);
    const calculatorInput = builderFormToCalculatorInput(form);
    const entryRonBeforeUplift = entryRonExVatBeforeCostUplift(
        entryRonWithUplifts,
        calculatorInput,
    );
    const stampDutyAmountRon = getStampDutyAmountRon(
        form.stampDutyAmount,
        form.stampDutyCurrency,
        pricingSettings,
    );
    const entryRonRaw = roundMoney(entryRonBeforeUplift - stampDutyAmountRon, 2);
    const finalEntry = finalEntryFromEntryPriceRonExVat(
        entryRonRaw > 0 ? entryRonRaw : entryRonBeforeUplift,
        form.currency,
        pricingSettings,
    );
    applyFinalEntryPriceEdit(form, finalEntry, pricingSettings);
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyEntryPriceRonIncVatEdit(form, value, pricingSettings) {
    const settings = mergeListPricingSettings(pricingSettings);
    const vatRate = parseNumericInput(settings.vatRate) || DEFAULT_PRICING_SETTINGS.vatRate;
    const entryRonExVat = vatRate > 0
        ? roundMoney(parseNumericInput(value) / vatRate, 2)
        : 0;
    applyEntryPriceRonExVatEdit(form, entryRonExVat, pricingSettings);
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyListPriceRonExVatEdit(form, value, pricingSettings) {
    const settings = mergeListPricingSettings(pricingSettings);
    const vatRate = parseNumericInput(settings.vatRate) || DEFAULT_PRICING_SETTINGS.vatRate;
    const listIncVat = vatRate > 0
        ? roundMoney(parseNumericInput(value) * vatRate, 2)
        : 0;
    applyListPriceRonIncVatFinalEdit(form, listIncVat, pricingSettings);
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyListPriceEuroExVatEdit(form, value, pricingSettings) {
    const listRonExVat = roundMoney(
        parseNumericInput(value) * LIST_PRICE_EURO_DIVISOR,
        2,
    );
    applyListPriceRonExVatEdit(form, listRonExVat, pricingSettings);
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyListPriceRonIncVatFinalEdit(form, value, pricingSettings) {
    const parsed = parseNumericInput(value);
    if (!(parsed > 0)) {
        clearListPriceOverride(form);
        form.markupActual = '';
        return;
    }
    form.overrideListPriceRonIncVatFinal = true;
    form.overrideListPriceRonIncVatFinalValue = formatNumericInputValue(parsed, 2);
    // Derive from new list vs entry money — not computedMarkupActual (stored % wins).
    const derived = deriveMarkupActualRatioFromListVsEntry(form, pricingSettings);
    form.markupActual = derived == null ? '' : ratioToPercentageInput(derived);
}

/**
 * Adaos actual drives list price: pret listă = pret intrare × (1 + adaos).
 * Clears any sticky list override so the calculator applies the formula;
 * listă remains manually editable and then re-derives adaos.
 *
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyMarkupActualEdit(form, value, pricingSettings) {
    void pricingSettings;
    const raw = value ?? form.markupActual;
    const trimmed = String(raw ?? '').trim();
    if (trimmed === '') {
        form.markupActual = '';
        clearListPriceOverride(form);
        return;
    }
    form.markupActual = formatNumericInputValue(parseNumericInput(raw), 2);
    clearListPriceOverride(form);
}

/**
 * Re-apply adaos actual so selling price recalculates from the new entry cost,
 * without clearing lockMarkupToReference (FX / supplier-% keep-markup must not
 * look like a manual adaos edit, and must not sync to referință).
 *
 * @param {Record<string, unknown>} form
 * @param {unknown} markupValue
 * @param {Record<string, unknown>} pricingSettings
 */
function reapplyMarkupActualKeepingLock(form, markupValue, pricingSettings) {
    applyMarkupActualEdit(form, markupValue, pricingSettings);
}

/**
 * Resolve the current selling price (RON cu TVA) for a furnizor tab / root form
 * so FX recalc can derive adaos when markupActual is empty in storage.
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} pricing
 * @param {unknown} [fallbackListPrice]
 * @returns {number}
 */
function resolveListPriceForExchangeRecalc(form, pricing, fallbackListPrice = null) {
    const candidates = [
        form.overrideListPriceRonIncVatFinal
            ? form.overrideListPriceRonIncVatFinalValue
            : null,
        pricing?.overrideListPriceRonIncVatFinal
            ? pricing.overrideListPriceRonIncVatFinalValue
            : null,
        pricing?.listPriceRonIncVatFinal,
        form.listPriceRonIncVatFinal,
        fallbackListPrice,
    ];
    for (const candidate of candidates) {
        const value = parseNumericInput(candidate);
        if (value > 0) {
            return value;
        }
    }
    return 0;
}

/**
 * Recalculate selling prices after a list exchange-rate change while keeping
 * each furnizor tab's adaos actual unchanged.
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} newPricingSettings
 * @param {Record<string, unknown>|null} [previousPricingSettings]
 *   Used to derive adaos at the pre-change entry price when markupActual is
 *   empty but a selling price exists (common on imported products).
 * @param {{
 *   fallbackListPrice?: unknown,
 *   affectedCurrencies?: Set<string>|string[],
 * }} [options]
 *   When `affectedCurrencies` is set, only tabs in those currencies are
 *   recalculated (e.g. only `euro` when EUR list rate changed).
 */
export function applyExchangeRateChangeKeepingMarkup(
    form,
    newPricingSettings,
    previousPricingSettings = null,
    options = {},
) {
    persistActiveFurnizorPricingFromRoot(form);
    const pricings = Array.isArray(form.furnizorPricings)
        ? [...form.furnizorPricings]
        : [];
    if (!pricings.length) {
        return;
    }
    const activeKey = form.activeFurnizorPricingKey;
    const settingsForDerive = previousPricingSettings || newPricingSettings;
    const fallbackListPrice = options.fallbackListPrice;
    const affectedCurrencies = options.affectedCurrencies instanceof Set
        ? options.affectedCurrencies
        : (
            Array.isArray(options.affectedCurrencies)
                ? new Set(options.affectedCurrencies)
                : null
        );

    pricings.forEach((pricing) => {
        applyFurnizorPricingToRoot(form, pricing);

        if (affectedCurrencies && affectedCurrencies.size) {
            const tabCurrency = normalizeCurrencyCode(form.currency);
            if (!affectedCurrencies.has(tabCurrency)) {
                // Unrelated FX (e.g. PLN tab when only EUR rate changed).
                persistActiveFurnizorPricingFromRoot(form);
                return;
            }
        }

        if (String(form.markupActual ?? '').trim() === '') {
            const listPrice = resolveListPriceForExchangeRecalc(
                form,
                pricing,
                pricing.isDefault ? fallbackListPrice : null,
            );
            if (listPrice > 0) {
                form.overrideListPriceRonIncVatFinal = true;
                form.overrideListPriceRonIncVatFinalValue = formatNumericInputValue(
                    listPrice,
                    2,
                );
                syncMarkupActualFromListOverride(form, settingsForDerive);
            }
        }

        const markup = form.markupActual;
        if (String(markup ?? '').trim() === '') {
            // Still no adaos (no selling price / no entry) — leave tab as-is.
            persistActiveFurnizorPricingFromRoot(form);
            return;
        }
        // Keep-markup wins over lock: same adaos → new selling price.
        // Do not sync to referință; do not clear the lock bifă.
        reapplyMarkupActualKeepingLock(form, markup, newPricingSettings);
        persistActiveFurnizorPricingFromRoot(form);
    });

    const restore = pricings.find((row) => row.clientKey === activeKey)
        || getDefaultFurnizorPricing(form)
        || pricings[0];
    if (restore) {
        applyFurnizorPricingToRoot(form, restore);
    }
}

/**
 * Ensure markupActual is derived from the current selling price before an
 * upstream entry-cost change that should keep adaos (FX / supplier % majorare).
 * Must run while the pre-change entry inputs are still on the form.
 *
 * Keep-markup exceptions ignore lockMarkupToReference: we still need a concrete
 * adaos to re-apply after the entry-cost change (never force referință here).
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} pricingSettings
 * @param {{ fallbackListPrice?: unknown }} [options]
 */
function ensureMarkupActualFromListPrice(form, pricingSettings, options = {}) {
    if (String(form.markupActual ?? '').trim() !== '') {
        return;
    }
    const listPrice = resolveListPriceForExchangeRecalc(
        form,
        {
            overrideListPriceRonIncVatFinal: form.overrideListPriceRonIncVatFinal,
            overrideListPriceRonIncVatFinalValue:
                form.overrideListPriceRonIncVatFinalValue,
            listPriceRonIncVatFinal: form.listPriceRonIncVatFinal,
        },
        options.fallbackListPrice,
    );
    if (!(listPrice > 0)) {
        return;
    }
    form.overrideListPriceRonIncVatFinal = true;
    form.overrideListPriceRonIncVatFinalValue = formatNumericInputValue(
        listPrice,
        2,
    );
    syncMarkupActualFromListOverride(form, pricingSettings);
}

/**
 * Apply a relative % change to pret listă furnizor while keeping adaos actual
 * and recalculating selling price — same policy as exchange-rate changes.
 *
 * Used by bulk "Preț listă furnizor" majorare/micșorare %.
 *
 * Exception vs lockMarkupToReference: even when the lock bifă is on, this path
 * keeps the current adaos actual (does NOT sync to referință).
 *
 * @param {Record<string, unknown>} form
 * @param {unknown} percentValue percent points (e.g. 10 for +10%, -10 for −10%)
 * @param {Record<string, unknown>} [pricingSettings]
 * @param {{ fallbackListPrice?: unknown }} [options]
 */
export function applySupplierListPricePercentChangeKeepingMarkup(
    form,
    percentValue,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
    options = {},
) {
    // Derive adaos from current listă before mutating supplier price (import
    // rows often have empty markupActual but a stored selling price).
    ensureMarkupActualFromListPrice(form, pricingSettings, options);

    const markupToKeep = form.markupActual;

    // Work in 2-dp money (same as lista în lucru display) so +10% does not
    // become 10,04% from 4-dp intermediate drift.
    const current = roundMoney(parseNumericInput(form.supplierListPriceNew), 2);
    const pct = parseNumericInput(percentValue);
    const factor = 1 + (pct / 100);
    const next = roundMoney(current * factor, 2);
    form.supplierListPriceOld = current > 0
        ? formatNumericInputValue(current, 2)
        : form.supplierListPriceOld;
    form.supplierListPriceNew = formatNumericInputValue(next, 2);

    syncTransportCostFromBreakdown(form);
    syncStampDutyFromBreakdown(form, pricingSettings);

    if (String(markupToKeep ?? '').trim() === '') {
        // No selling price / no adaos — only supplier list price changed.
        return;
    }
    // Keep-markup wins over lock: same adaos → new selling price (like FX).
    // Do not sync to referință; do not clear the lock bifă.
    reapplyMarkupActualKeepingLock(form, markupToKeep, pricingSettings);
}

/**
 * Upstream edits that change entry price must keep an existing list price sticky
 * and recompute adaos actual from list vs entry. If no list price exists yet
 * (new product / only supplier price filled), do not invent one.
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} pricingSettings
 * @param {unknown} preservedListPriceRonIncVatFinal
 * @param {{ markupActualManual?: boolean }} result
 */
function keepListPriceStickyAndSyncMarkup(
    form,
    pricingSettings,
    preservedListPriceRonIncVatFinal,
    result,
) {
    if (isMarkupLockedToReference(form)) {
        // Purchase params changed under lock — unlock must not restore pre-lock adaos.
        clearLockMarkupRestoreSnapshot(form);
        syncMarkupActualToReference(form);
        result.markupActualManual = false;
        return;
    }
    let preserved = preservedListPriceRonIncVatFinal;
    if (!(parseNumericInput(preserved) > 0)) {
        const existingOverride = parseNumericInput(
            form.overrideListPriceRonIncVatFinalValue,
        );
        if (form.overrideListPriceRonIncVatFinal && existingOverride > 0) {
            preserved = existingOverride;
        }
    }
    if (!(parseNumericInput(preserved) > 0)) {
        // No selling price yet — leave list empty; do not sync −100% adaos.
        result.markupActualManual = false;
        return;
    }
    preserveListPriceOverride(form, preserved);
    syncMarkupActualFromListOverride(form, pricingSettings);
    result.markupActualManual = false;
}

/**
 * After lock→unlock with interim purchase edits: sticky the current selling
 * price so the next upstream edit keeps it, but leave markupActual untouched
 * (it already equals referință — re-deriving from 2-dp list money drifts %).
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} pricingSettings
 * @param {unknown} preservedListPriceRonIncVatFinal
 * @param {{ markupActualManual?: boolean }} result
 */
function resumeStickyListPriceAfterLockWithoutResyncingMarkup(
    form,
    pricingSettings,
    preservedListPriceRonIncVatFinal,
    result,
) {
    let preserved = preservedListPriceRonIncVatFinal;
    if (!(parseNumericInput(preserved) > 0)) {
        const existingOverride = parseNumericInput(
            form.overrideListPriceRonIncVatFinalValue,
        );
        if (form.overrideListPriceRonIncVatFinal && existingOverride > 0) {
            preserved = existingOverride;
        }
    }
    if (!(parseNumericInput(preserved) > 0)) {
        const computed = calculateFromForm(form, pricingSettings);
        preserved = computed.listPriceRonIncVatFinal;
    }
    if (parseNumericInput(preserved) > 0) {
        preserveListPriceOverride(form, preserved);
    }
    result.markupActualManual = false;
}

/**
 * Applies a manual edit to a pricing field and updates dependent fields using
 * the existing forward/inverse calculation rules.
 *
 * @param {Record<string, unknown>} form
 * @param {string} fieldKey
 * @param {unknown} rawValue
 * @param {Record<string, unknown>} [pricingSettings]
 * @param {{
 *   preservedListPriceRonIncVatFinal?: unknown,
 *   convertSupplierListPriceNew?: boolean,
 *   convertSupplierListPriceOld?: boolean,
 * }} [options]
 * @returns {{ markupActualManual?: boolean }}
 */
export function applyPricingFieldEdit(
    form,
    fieldKey,
    rawValue,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
    options = {},
) {
    const result = {};
    const preservedListPrice = options.preservedListPriceRonIncVatFinal;

    switch (fieldKey) {
        case 'lockMarkupToReference':
            form.lockMarkupToReference = Boolean(rawValue);
            if (form.lockMarkupToReference) {
                // Lock ON: remember prior adaos/sticky so a plain uncheck is a no-op.
                captureLockMarkupRestoreSnapshot(form);
                syncMarkupActualToReference(form);
                result.markupActualManual = false;
            } else if (restoreLockMarkupSnapshot(form)) {
                // Lock OFF without interim purchase edits: put back exact prior values.
                result.markupActualManual = false;
            } else {
                // Lock OFF after purchase edits under lock: keep current adaos
                // (already = referință) and sticky the current selling price.
                // Do NOT re-derive adaos from sticky 2-dp money — that turns
                // exact 36% into 35,99% (list/entry rounding).
                resumeStickyListPriceAfterLockWithoutResyncingMarkup(
                    form,
                    pricingSettings,
                    preservedListPrice,
                    result,
                );
            }
            break;
        case 'markupReference':
            if (isMarkupLockedToReference(form)) {
                clearLockMarkupRestoreSnapshot(form);
                syncMarkupActualToReference(form);
                result.markupActualManual = false;
            }
            break;
        case 'markupActual':
            // Adaos actual drives pret listă RON (cu/fără TVA) via formula;
            // listă remains manually editable and then re-derives adaos.
            applyMarkupActualEdit(form, rawValue, pricingSettings);
            form.lockMarkupToReference = false;
            clearLockMarkupRestoreSnapshot(form);
            result.markupActualManual = true;
            break;
        case 'transportCostRate':
            // Keep formula breakdown; manual % overrides computed rate only.
            {
                const breakdown = normalizeTransportCostBreakdown(
                    form.transportCostBreakdown,
                );
                if (breakdown.mode === TRANSPORT_COST_MODE_FORMULA) {
                    breakdown.rateIsManual = true;
                    form.transportCostBreakdown = breakdown;
                }
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'transportCostBreakdown':
            if (rawValue && typeof rawValue === 'object') {
                const breakdown = normalizeTransportCostBreakdown(rawValue);
                // Formula apply recomputes %; percent/reset keeps an explicit rate.
                if (breakdown.mode === TRANSPORT_COST_MODE_FORMULA) {
                    breakdown.rateIsManual = false;
                }
                form.transportCostBreakdown = breakdown;
            }
            syncTransportCostFromBreakdown(form);
            if (
                form.transportCostBreakdown?.mode === TRANSPORT_COST_MODE_PERCENT
                && String(form.transportCostRate ?? '').trim() === ''
            ) {
                // Reset / clear formula must land on 0%, not empty (−100% hint).
                form.transportCostRate = '0';
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'stampDutyAmount':
            {
                const breakdown = normalizeStampDutyBreakdown(
                    form.stampDutyBreakdown,
                );
                if (breakdown.mode === STAMP_DUTY_MODE_FORMULA) {
                    breakdown.amountIsManual = true;
                    form.stampDutyBreakdown = breakdown;
                }
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'stampDutyCurrency':
            form.stampDutyCurrency = normalizeStampDutyCurrency(
                rawValue || form.stampDutyCurrency || 'ron',
            );
            {
                const breakdown = normalizeStampDutyBreakdown(
                    form.stampDutyBreakdown,
                );
                if (breakdown.mode === STAMP_DUTY_MODE_FORMULA) {
                    // Manual currency override while formula is open — keep amount,
                    // mark amount manual so formula does not overwrite currency.
                    breakdown.amountIsManual = true;
                    form.stampDutyBreakdown = breakdown;
                }
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'stampDutyBreakdown':
            if (rawValue && typeof rawValue === 'object') {
                const breakdown = normalizeStampDutyBreakdown(rawValue);
                if (breakdown.mode === STAMP_DUTY_MODE_FORMULA) {
                    breakdown.amountIsManual = false;
                }
                form.stampDutyBreakdown = breakdown;
            }
            syncStampDutyFromBreakdown(form, pricingSettings);
            if (
                form.stampDutyBreakdown?.mode === STAMP_DUTY_MODE_MANUAL
                && String(form.stampDutyAmount ?? '').trim() === ''
            ) {
                form.stampDutyAmount = '0';
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'supplierListPriceNew':
            syncTransportCostFromBreakdown(form);
            syncStampDutyFromBreakdown(form, pricingSettings);
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'discounts':
            if (Array.isArray(rawValue)) {
                form.discounts = [...rawValue];
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'costUplifts':
            if (Array.isArray(rawValue)) {
                form.costUplifts = [...rawValue];
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'discount1':
        case 'discount2':
        case 'cashDiscount':
        case 'marketingDiscount':
        case 'squareMetersPerTruck':
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'currency': {
            // Caller should leave form.currency as the previous currency
            // (use :model-value, not v-model) so we can convert list prices.
            // If the user already retyped pret lista in the target currency,
            // skip converting that field (convertSupplierListPriceNew: false).
            const previousCurrency = String(form.currency || 'ron');
            const nextCurrency = String(rawValue || 'ron');
            convertSupplierListPricesForCurrencyChange(
                form,
                previousCurrency,
                nextCurrency,
                pricingSettings,
                {
                    convertNew: options.convertSupplierListPriceNew !== false,
                    convertOld: options.convertSupplierListPriceOld !== false,
                },
            );
            form.currency = nextCurrency;
            syncTransportCostFromBreakdown(form);
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        }
        case 'netEntryPriceExVat':
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            applyNetEntryPriceEdit(form, rawValue, pricingSettings);
            syncMarkupActualFromListOverride(form, pricingSettings);
            break;
        case 'finalEntryPriceExVat':
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            applyFinalEntryPriceEdit(form, rawValue, pricingSettings);
            syncMarkupActualFromListOverride(form, pricingSettings);
            break;
        case 'entryPriceRonExVat':
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            applyEntryPriceRonExVatEdit(form, rawValue, pricingSettings);
            syncMarkupActualFromListOverride(form, pricingSettings);
            break;
        case 'entryPriceRonIncVat':
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            applyEntryPriceRonIncVatEdit(form, rawValue, pricingSettings);
            syncMarkupActualFromListOverride(form, pricingSettings);
            break;
        case 'listPriceRonExVat':
            applyListPriceRonExVatEdit(form, rawValue, pricingSettings);
            form.lockMarkupToReference = false;
            clearLockMarkupRestoreSnapshot(form);
            result.markupActualManual = true;
            break;
        case 'listPriceEuroExVat':
            applyListPriceEuroExVatEdit(form, rawValue, pricingSettings);
            form.lockMarkupToReference = false;
            clearLockMarkupRestoreSnapshot(form);
            result.markupActualManual = true;
            break;
        case 'listPriceRonIncVatFinal':
            applyListPriceRonIncVatFinalEdit(form, rawValue, pricingSettings);
            form.lockMarkupToReference = false;
            clearLockMarkupRestoreSnapshot(form);
            result.markupActualManual = true;
            break;
        default:
            break;
    }

    return result;
}

/**
 * Computed selling price (list RON inc. VAT) for a specific furnizor pricing row.
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} pricing
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {number}
 */
export function computeFurnizorListPriceRonIncVatFinal(
    form,
    pricing,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
) {
    const computed = calculateProductPricing(
        builderFormToCalculatorInput(mergeFormWithFurnizorPricing(form, pricing)),
        mergeListPricingSettings(pricingSettings),
    );
    return parseNumericInput(computed.listPriceRonIncVatFinal);
}

/** Half a cent: sale prices within this gap count as the same client price. */
const FURNIZOR_SELLING_PRICE_TOLERANCE = 0.005;

/**
 * Final client sale price (RON cu TVA) on each furnizor tab of one product.
 * The active tab must already be persisted onto `furnizorPricings`.
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {Array<{
 *   clientKey: string,
 *   furnizorName: string,
 *   furnizorCode: string,
 *   usesManufacturerAsFurnizor: boolean,
 *   price: number,
 * }>}
 */
export function listFurnizorFinalSellingPrices(
    form,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
) {
    const pricings = Array.isArray(form?.furnizorPricings) ? form.furnizorPricings : [];
    return pricings.filter(Boolean).map((pricing) => ({
        clientKey: pricing.clientKey,
        furnizorName: pricing.furnizorName,
        furnizorCode: pricing.furnizorCode,
        usesManufacturerAsFurnizor: Boolean(pricing.usesManufacturerAsFurnizor),
        price: computeFurnizorListPriceRonIncVatFinal(
            form,
            pricing,
            pricingSettings,
        ),
    }));
}

/**
 * Same product code cannot be sold at different final prices per supplier.
 * One furnizor, or prices within half a cent, is not a mismatch.
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {boolean}
 */
export function furnizorFinalSellingPricesDiffer(
    form,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
) {
    const rows = listFurnizorFinalSellingPrices(form, pricingSettings);
    if (rows.length < 2) {
        return false;
    }
    const anchor = rows[0].price;
    return rows.some(
        (row) => Math.abs(row.price - anchor) >= FURNIZOR_SELLING_PRICE_TOLERANCE,
    );
}

/**
 * True when draft save should offer copying the active selling price to other
 * furnizor tabs (multi-supplier product + selling price changed + others differ).
 *
 * @param {Record<string, unknown>} form
 * @param {number|string|null|undefined} activeListPrice
 * @param {number|string|null|undefined} sessionBaselineListPrice
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {boolean}
 */
export function shouldOfferPropagateSellingPriceToOtherFurnizors(
    form,
    activeListPrice,
    sessionBaselineListPrice,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
) {
    const pricings = Array.isArray(form?.furnizorPricings) ? form.furnizorPricings : [];
    if (pricings.length < 2) {
        return false;
    }
    const activePrice = parseNumericInput(activeListPrice);
    if (!(activePrice > 0)) {
        return false;
    }
    const baselinePrice = parseNumericInput(sessionBaselineListPrice);
    if (Math.abs(activePrice - baselinePrice) < FURNIZOR_SELLING_PRICE_TOLERANCE) {
        return false;
    }
    const activeKey = form.activeFurnizorPricingKey;
    return pricings.some((pricing) => {
        if (!pricing || pricing.clientKey === activeKey) {
            return false;
        }
        const otherPrice = computeFurnizorListPriceRonIncVatFinal(
            form,
            pricing,
            pricingSettings,
        );
        return Math.abs(otherPrice - activePrice) >= FURNIZOR_SELLING_PRICE_TOLERANCE;
    });
}

/**
 * Copies the given selling price onto every non-active furnizor pricing row,
 * recalculates each row's adaos actual from its own entry price, and clears
 * „adaos actual = adaos referință” because the two are no longer tied.
 *
 * @param {Record<string, unknown>} form
 * @param {number|string} listPriceRonIncVatFinal
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {string[]} clientKeys that were updated
 */
export function applySellingPriceToOtherFurnizorPricings(
    form,
    listPriceRonIncVatFinal,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
) {
    persistActiveFurnizorPricingFromRoot(form);
    const pricings = Array.isArray(form.furnizorPricings) ? form.furnizorPricings : [];
    const activeKey = form.activeFurnizorPricingKey;
    const priceText = formatNumericInputValue(
        parseNumericInput(listPriceRonIncVatFinal),
        2,
    );
    if (!(parseNumericInput(priceText) > 0) || pricings.length < 2) {
        return [];
    }

    const updatedKeys = [];
    pricings.forEach((pricing) => {
        if (!pricing || pricing.clientKey === activeKey) {
            return;
        }
        const merged = mergeFormWithFurnizorPricing(form, pricing);
        applyPricingFieldEdit(
            merged,
            'listPriceRonIncVatFinal',
            priceText,
            pricingSettings,
        );
        pricing.overrideListPriceRonIncVatFinal =
            merged.overrideListPriceRonIncVatFinal;
        pricing.overrideListPriceRonIncVatFinalValue =
            merged.overrideListPriceRonIncVatFinalValue;
        pricing.useManualFinalPrice = merged.useManualFinalPrice;
        pricing.manualListPriceRonIncVatFinal =
            merged.manualListPriceRonIncVatFinal;
        pricing.markupActual = merged.markupActual;
        pricing.priceAdjustmentRatio = merged.priceAdjustmentRatio;
        // Forced sale price no longer matches adaos referință.
        pricing.lockMarkupToReference = false;
        clearLockMarkupRestoreSnapshot(pricing);
        updatedKeys.push(pricing.clientKey);
    });
    return updatedKeys;
}
